import { useState, useEffect, useCallback } from "react";
import { Typography, Button, Table, Tag, Space, Input, InputNumber, App, Popconfirm } from "antd";
import { getFunctions, httpsCallable } from "firebase/functions";
import { app } from "@/config/firebase";

const functions = getFunctions(app, "europe-west1");

const { Title, Text } = Typography;

interface BetaCodeRow {
  code: string;
  status: "available" | "used" | "revoked";
  createdAt: string | null;
  assignedTo: string | null;
  assignedAt: string | null;
  note: string | null;
}

const generateBetaCodeFn = httpsCallable<
  { count?: number; note?: string },
  { codes: string[] }
>(functions, "generateBetaCode");

const listBetaCodesFn = httpsCallable<
  Record<string, never>,
  { codes: BetaCodeRow[] }
>(functions, "listBetaCodes");

const revokeBetaCodeFn = httpsCallable<
  { code: string },
  { success: boolean }
>(functions, "revokeBetaCode");

export default function BetaCodesPage() {
  const { message } = App.useApp();
  const [codes, setCodes] = useState<BetaCodeRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [genCount, setGenCount] = useState(1);
  const [genNote, setGenNote] = useState("");

  const loadCodes = useCallback(async () => {
    setLoading(true);
    try {
      const result = await listBetaCodesFn({});
      setCodes(result.data.codes);
    } catch {
      message.error("Failed to load beta codes");
    } finally {
      setLoading(false);
    }
  }, [message]);

  useEffect(() => { loadCodes(); }, [loadCodes]);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const result = await generateBetaCodeFn({ count: genCount, note: genNote || undefined });
      message.success(`Generated ${result.data.codes.length} code(s): ${result.data.codes.join(", ")}`);
      setGenNote("");
      await loadCodes();
    } catch {
      message.error("Failed to generate codes");
    } finally {
      setGenerating(false);
    }
  };

  const handleRevoke = async (code: string) => {
    try {
      await revokeBetaCodeFn({ code });
      message.success(`Code ${code} revoked`);
      await loadCodes();
    } catch {
      message.error("Failed to revoke code");
    }
  };

  const statusColors: Record<string, string> = {
    available: "green",
    used: "blue",
    revoked: "red",
  };

  const columns = [
    {
      title: "Code",
      dataIndex: "code",
      key: "code",
      render: (code: string) => <Text copyable code>{code}</Text>,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status: string) => <Tag color={statusColors[status]}>{status}</Tag>,
    },
    {
      title: "Assigned To",
      dataIndex: "assignedTo",
      key: "assignedTo",
      render: (uid: string | null) => uid ? <Text code style={{ fontSize: 11 }}>{uid}</Text> : "—",
    },
    {
      title: "Created",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (v: string | null) => v ? new Date(v).toLocaleDateString() : "—",
    },
    {
      title: "Note",
      dataIndex: "note",
      key: "note",
      render: (v: string | null) => v || "—",
    },
    {
      title: "",
      key: "actions",
      render: (_: unknown, record: BetaCodeRow) => {
        if (record.status !== "available") return null;
        return (
          <Popconfirm title="Revoke this code?" onConfirm={() => handleRevoke(record.code)}>
            <Button size="small" danger>Revoke</Button>
          </Popconfirm>
        );
      },
    },
  ];

  const available = codes.filter((c) => c.status === "available").length;
  const used = codes.filter((c) => c.status === "used").length;

  return (
    <div>
      <Title level={4}>Beta Codes</Title>
      <Text type="secondary" style={{ display: "block", marginBottom: 16 }}>
        {codes.length} total — {available} available, {used} used
      </Text>

      <Space style={{ marginBottom: 16 }}>
        <InputNumber min={1} max={50} value={genCount} onChange={(v) => setGenCount(v || 1)} />
        <Input
          placeholder="Note (optional)"
          value={genNote}
          onChange={(e) => setGenNote(e.target.value)}
          style={{ width: 200 }}
        />
        <Button type="primary" onClick={handleGenerate} loading={generating}>
          Generate
        </Button>
      </Space>

      <Table
        dataSource={codes}
        columns={columns}
        rowKey="code"
        loading={loading}
        size="small"
        pagination={{ pageSize: 20 }}
      />
    </div>
  );
}
